import React, { Component } from "react";
import { MerchantGroup } from "../../models/MerchantModel";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { Column, ColumnEditorOptions } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { merchantGroupsApiService } from "../../services/api/merchants/MerchantGroupsApiService";
import { toastService } from "../../services/toastService";
import Loader from "../../components/Loader";
import { confirmDanger } from "../../utils/confirmHelpers";
import ExtendedInputText from "../../components/ExtendedInputText";
import { isMerchantProfileUpdateAllowed } from "../../authGuard";
import { Plus, X } from "lucide-react";

interface MerchantGroupProps {
    merchantId: number;
}

interface MerchantGroupState {
    groups: MerchantGroup[];
    isLoading: boolean;
    hoveredRow: number | null;
}

export default class MerchantGroupsComponent extends Component<MerchantGroupProps, MerchantGroupState> {
    validationErrors: Map<string, boolean> = new Map<string, boolean>();
    constructor(props: MerchantGroupProps | Readonly<MerchantGroupProps>) {
        super(props);

        this.state = {
            groups: [],
            isLoading: true,
            hoveredRow: null,
        };
    }

    textEditor(options: ColumnEditorOptions) {
        return (
            <ExtendedInputText
                type="text"
                value={options.value}
                onChange={(value) => options.editorCallback && options.editorCallback(value)}
                name={options.field}
                required={true}
                onValidStatusChanged={(value: boolean) => this.validationErrors.set(options.field, value)}
            />
        );
    }

    onRowHover = (rowData: MerchantGroup) => {
        this.setState({ hoveredRow: rowData.id });
    };

    onRowHoverLeave = () => {
        this.setState({ hoveredRow: null });
    };

    async componentDidMount() {
        this.loadData();
    }
    loadData = async () => {
        this.setState({ isLoading: true });

        const groupResult = await merchantGroupsApiService.getAll(this.props.merchantId);
        this.setState({ isLoading: false });
        if (!groupResult.success) {
            toastService.error(groupResult.message);
            return;
        }
        console.log(groupResult.data);
        this.setState({ groups: groupResult.data });
        this.setState({ isLoading: false });
    };

    onRowEditComplete = async (e: DataTableRowEditCompleteEvent) => {
        this.setState({ isLoading: true });

        const newData = e.newData as MerchantGroup;

        const result = newData.id != 0 ? await merchantGroupsApiService.update(newData) : await merchantGroupsApiService.create(newData);

        this.setState({ isLoading: false });

        if (!result.success) {
            toastService.error(result.message);
            return;
        }

        this.loadData();
    };

    addGroup = () => {
        const groups = this.state.groups;
        groups.push({ id: 0, name: "", totalUsers: 0, merchantId: this.props.merchantId, tariffId: 1, isDefault: false });
        this.setState({ groups: groups });
    };

    renderHeader = () => {
        return (
            <div className="table-header">
                <span className="p-input-icon-left align-right">
                    {isMerchantProfileUpdateAllowed() && (
                        <Button label="Add Group" outlined icon={<Plus size={16} />} onClick={this.addGroup} />
                    )}
                </span>
            </div>
        );
    };

    confirmDeleteGroup = async (rowData: MerchantGroup) => {
        if (rowData.id === 0) {
            this.setState({ groups: this.state.groups.filter((group) => group.id !== rowData.id) });
            return;
        }

        confirmDanger(
            "This action will remove the group and is irreversible. Are you sure you want to proceed?",
            "Delete Group",
            () => this.deleteGroup(rowData),
        );
    };

    changeMerchantStatus = async (rowData: MerchantGroup) => {
        this.setState({ isLoading: true });
        if (rowData.id === 0) {
            this.setState({ groups: this.state.groups.filter((group) => group.id !== rowData.id) });
            return;
        }
        const result = await merchantGroupsApiService.changeDefaultMerchant(rowData.id);

        if (!result.success) {
            this.setState({ isLoading: false });
            toastService.error(result.message);
        }
        this.loadData();
    };

    deleteGroup = async (rowData: MerchantGroup) => {
        const deleteResult = await merchantGroupsApiService.delete(rowData.id);

        if (deleteResult.success) {
            this.setState({ groups: this.state.groups.filter((group) => group.id !== rowData.id) });
        } else {
            toastService.error(deleteResult.message);
        }
    };

    actionBodyTemplate = (rowData: MerchantGroup) => {
        const isHovered = this.state.hoveredRow === rowData.id;

        return (
            <div className="d-flex align-items-center">
                <button type="button" className="inline-flex items-center justify-center rounded-md p-2 hover:bg-neutral-100 transition-colors m-2" tabIndex={0} onClick={() => this.confirmDeleteGroup(rowData)}>
                    <X size={16} className="cursor-pointer text-neutral-500 hover:text-danger-500 transition-colors" />
                </button>

                <Button
                    label="Set Default Merchant"
                    type="button"
                    size="small"
                    className="m-2 text-nowrap"
                    onClick={() => this.changeMerchantStatus(rowData)}
                />
            </div>
        );
    };

    render() {
        return (
            <div>
                    <DataTable
                        header={this.renderHeader}
                        value={this.state.groups}
                        editMode="row"
                        dataKey="id"
                        loading={this.state.isLoading}
                        onRowEditComplete={this.onRowEditComplete}
                    >
                        <Column field="id" header="Id"></Column>
                        <Column field="name" header="Name" editor={(options) => this.textEditor(options)} style={{ width: "20%" }}></Column>
                        <Column field="totalUsers" header="Total Customers"></Column>
                        <Column field="isDefault" header="Is Default Merchant"></Column>
                        <Column rowEditor headerStyle={{ width: "10%", minWidth: "8rem" }} bodyStyle={{ textAlign: "center" }}></Column>
                        <Column
                            header=""
                            body={this.actionBodyTemplate}
                            headerStyle={{ width: "8em", textAlign: "center" }}
                            bodyStyle={{ textAlign: "center", overflow: "visible" }}
                        ></Column>
                    </DataTable>
            </div>
        );
    }
}
